import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Loads something from the API and reports the three states a screen must draw.
 *
 * Small on purpose. A data-fetching library would earn its place once there is
 * caching or invalidation to do across screens; today every screen loads what
 * it shows and reloads it after a write, and `AGENTS.md §4` asks for the
 * abstraction the callers actually need rather than the one they might.
 *
 * `load` is held in a ref so that an inline arrow at the call site does not
 * restart the request on every render. `key` is what says the request has
 * genuinely changed.
 */
export type Resource<T> =
  | { status: 'loading'; data: T | null; error: null }
  | { status: 'ready'; data: T; error: null }
  | { status: 'failed'; data: null; error: unknown };

export interface ResourceHandle<T> {
  resource: Resource<T>;
  reload: () => void;
  /** Replaces the loaded value after a write, without a round trip. */
  replace: (value: T) => void;
}

export const useResource = <T>(key: string, load: () => Promise<T>): ResourceHandle<T> => {
  const [resource, setResource] = useState<Resource<T>>({
    status: 'loading',
    data: null,
    error: null,
  });
  const [reloadCount, setReloadCount] = useState(0);

  const latestLoad = useRef(load);
  latestLoad.current = load;

  // biome-ignore lint/correctness/useExhaustiveDependencies: key and reloadCount are the triggers, not values the body reads
  useEffect(() => {
    let cancelled = false;
    setResource((previous) => ({ status: 'loading', data: previous.data, error: null }));

    latestLoad
      .current()
      .then((data) => {
        if (!cancelled) {
          setResource({ status: 'ready', data, error: null });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setResource({ status: 'failed', data: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [key, reloadCount]);

  const reload = useCallback(() => {
    setReloadCount((count) => count + 1);
  }, []);

  const replace = useCallback((value: T) => {
    setResource({ status: 'ready', data: value, error: null });
  }, []);

  return { resource, reload, replace };
};
